<script>
import { toDecPercent } from '@utils/Format'
import { WALLET_EMPTY_MESSAGE } from '@modules/consolidator/constants/Text'
import FeedbackWrapper from '@modules/global/components/feedback-wrapper/feedback-wrapper.vue'
import { getBenchmarkStats } from '@modules/consolidator/utils/get-benchmark-stats/get-benchmark-stats'

export default {
  name: 'Estatisticas',
  components: { FeedbackWrapper },
  props: {
    hideVerMais: { type: Boolean, default: false },
    metricas: {
      type: Object,
      default: () => ({}),
    },
    rentabilidade: {
      type: Object,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      cardMinHeight: 520,
      WALLET_EMPTY_MESSAGE,
    }
  },
  computed: {
    analisePortifolio() {
      const metricasPortfolio = {
        rent: {
          topic: 'Rentabilidade',
          info: 'Rentabilidade desde o início da carteira',
        },
        vol: {
          topic: 'Volatilidade',
          info: 'Volatilidade é uma medida de risco. Com 95% de confiança, seu portfólio poderá variar 2 vezes (1,96) para mais ou para menos',
        },
        sharpe: {
          topic: 'Sharpe',
          info: 'Retorno da carteira menos Retorno do CDI dividido pelo risco. Dessa forma, você consegue comparar quanto de retorno você tem ajustado ao risco',
        },
      }

      const defaultValue = '-'

      return Object.keys(metricasPortfolio).map((key) => ({
        topic: metricasPortfolio[key].topic,
        value: this.metricas[key] || defaultValue,
        info: metricasPortfolio[key].info,
      }))
    },
    rentRelativa() {
      const orderedBenchmarks = [
        'CDI',
        'IFIX',
        'IPC-A',
        'IRF-M',
        'IBOVESPA',
        'DÓLAR',
        'IMA-B',
        'S&P 500',
      ]

      const shouldUseDefaultValues =
        !this.rentabilidade ||
        !this.rentabilidade.benchmarks_statistics ||
        !this.$store.getters['settings/benchmarks']

      if (shouldUseDefaultValues) {
        return orderedBenchmarks.map((benchmarkName) => ({
          text: benchmarkName,
          value: '-',
        }))
      }

      const benchs = this.rentabilidade.benchmarks_statistics
      const rent = this.rentabilidade.inicio
      const benchmarks = this.$store.getters['settings/benchmarks']

      return orderedBenchmarks.map((benchmarkName) => {
        const benchmark = benchmarks.find((b) => b.nome === benchmarkName)
        return benchmark
          ? {
              text: benchmark.nome,
              value: this.calculateBenchmarkValue(benchmark, benchs, rent),
            }
          : { text: benchmarkName, value: '-' }
      })
    },
  },
  methods: {
    calculateBenchmarkValue(benchmark, benchs, rent) {
      const benchmarkStats = getBenchmarkStats(benchs, benchmark)

      const isBenchmarkMissing = !benchmarkStats
      const isRentInicioInvalid =
        benchmarkStats && benchmarkStats.rent_inicio == null
      const isRentInvalid = rent == null

      if (isBenchmarkMissing || isRentInicioInvalid || isRentInvalid) {
        return '-'
      }

      if (benchmark.nome === 'CDI') {
        const cdiRatio = rent / benchmarkStats.rent_inicio
        return toDecPercent(cdiRatio)
      }

      const diffRatio = rent - benchmarkStats.rent_inicio
      return (diffRatio >= 0 ? '+' : '') + toDecPercent(diffRatio)
    },
    toAnalisePortf() {
      this.$router.push({ name: 'carteira.analise' })
    },
  },
}
</script>

<template>
  <div
    class="estatisticas az-card-bordered"
    :class="{ 'estatisticas--loading': loading }"
  >
    <FeedbackWrapper
      :loading="loading"
      :message="WALLET_EMPTY_MESSAGE"
      :minHeight="cardMinHeight"
    >
      <div class="estatisticas__header">
        <span class="az-ds-card-title"> Estatísticas </span>
        <v-tooltip right max-width="360">
          <template v-slot:activator="{ on, attrs }">
            <icon-svg name="mdi-information" size="16" color="#8E8E8E" v-bind="attrs" v-on="on" />
          </template>
          <span>
            A carteira atual é cotizada com base nos ativos e movimentações que
            você/seu cliente cadastra na plataforma. O sistema nivela a análise
            em D-3, conforme disponibilização das cotas dos fundos pela CVM. As
            rotinas de atualização da cotização são feitas diariamente,
            automaticamente.
          </span>
        </v-tooltip>
      </div>

      <div class="estatisticas__portfolio">
        <div
          v-for="(item, index) in analisePortifolio"
          :key="index"
          class="estatisticas__portfolio-item"
        >
          <div class="d-flex align-center">
            <span class="az-text-primary az-size-14 font-semibold mr-1">{{ item.topic }}</span>
            <v-tooltip right max-width="360">
              <template v-slot:activator="{ on, attrs }">
                <icon-svg name="mdi-information" size="14" color="#8E8E8E" v-bind="attrs" v-on="on" />
              </template>
              <span>
                {{ item.info }}
              </span>
            </v-tooltip>
          </div>
          <div class="estatisticas__portfolio-text az-text-primary az-size-14 font-bold">
            {{ item.value }}
          </div>
        </div>

        <BaseButton
          v-if="!hideVerMais"
          secondary
          text
          sm
          small
          class="estatisticas__portfolio-button"
          @click="toAnalisePortf"
        >
          <icon-svg name="mdi-eye" size="20" color="primary" />
          Ver mais
        </BaseButton>
      </div>

      <div class="estatisticas__rentabilidade">
        <div class="az-text-primary az-size-14 font-semibold">Rentabilidade Relativa</div>
        <div class="estatisticas__rentabilidade-container">
          <div
            v-for="(item, index) in rentRelativa"
            :key="index"
            class="estatisticas__rentabilidade-item"
          >
            <span
              class="estatisticas__rentabilidade-text az-text-primary az-size-14 az-muted"
            >
              <template v-if="item.text === 'CDI'">
                <strong>{{ item.value }}</strong>
                <span>{{ item.text }}</span>
              </template>
              <template v-else>
                <span>{{ item.text }}</span>
                <strong>{{ item.value }}</strong>
              </template>
            </span>
          </div>
        </div>
      </div>
      <slot name="nota" />
    </FeedbackWrapper>
  </div>
</template>

<style lang="sass" scoped>
$spacing-md: 16px
$spacing-lg: 24px

.estatisticas
  &--loading
    padding: 0 !important

  &__header
    display: flex
    margin-bottom: 17px
    gap: 4px
    align-items: center
    justify-content: flex-start

  &__portfolio
    display: flex
    flex-direction: column
    margin-bottom: 34px
    width: 100%

    &-item
      display: flex
      justify-content: space-between
      padding: $spacing-md $spacing-lg
      background-color: var(--neutral-100)

      &:nth-child(even)
        background-color: var(--neutral-50)

    &-button
      margin-left: auto
      margin-top: 8px
      min-width: unset !important

  &__rentabilidade
    &-container
      display: grid
      grid-template-columns: repeat(2, 1fr)

    &-item
      padding: $spacing-md 0px
      border-bottom: 1px solid var(--border-hairline)

      &:nth-child(even)
        text-align: end
        padding-right: $spacing-lg

      &:nth-last-child(1), &:nth-last-child(2)
        border-bottom: none

    &-text
      display: inline-flex
      align-items: center
      gap: 8px
</style>
